<template>
    <ModelIndexLayout
        :modelNamePlural="__('admin::texts.texts')"
    >

        <form class="mb-6" @submit.prevent>
            <div class="flex justify-between items-center">

                <div class="flex-1 mr-2">
                    <div class="relative rounded-md shadow-sm">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-sm text-gray-400">
                            {{ __('forms::labels.from') }}:
                        </div>
                        <select name="locale_from" class="block w-full box-border pl-16 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" v-model="filter.locale_from" @change="updateResults">
                            <option :value="k" v-for="(v, k) in localeOptions" :key="k">{{ v }}</option>
                        </select>
                    </div>
                </div>

                <div class="flex-1 mr-2">
                    <div class="relative rounded-md shadow-sm">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-sm text-gray-400">
                            {{ __('forms::labels.to') }}:
                        </div>
                        <select name="locale_to" class="block w-full box-border pl-12 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" v-model="filter.locale_to" @change="updateResults">
                            <option :value="k" v-for="(v, k) in localeOptions" :key="k">{{ v }}</option>
                        </select>
                    </div>
                </div>

                <div class="flex-1 mr-2">
                    <div class="relative rounded-md shadow-sm">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-sm text-gray-400">
                            <EyeIcon class="h-5 w-5 text-gray-400" />
                        </div>
                        <select name="scope" class="block w-full box-border pl-12 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" v-model="filter.scope" @change="updateResults">
                            <option :value="k" v-for="(v, k) in scopeOptions" :key="k">{{ v }}</option>
                        </select>
                    </div>
                </div>

                <div class="flex-1 mr-2">
                    <div class="relative rounded-md shadow-sm">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-sm text-gray-400">
                            <AdjustmentsIcon class="h-5 w-5 text-gray-400" />
                        </div>
                        <select name="namespace" class="block w-full box-border pl-12 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" v-model="filter.namespace" @change="updateResults">
                            <option :value="v" v-for="(v) in namespaceOptions" :key="v">{{ v }}</option>
                        </select>
                    </div>
                </div>

                <div class="flex-1">

                    <div class="relative rounded-md shadow-sm">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon class="h-5 w-5 text-gray-400" />
                        </div>
                        <Input :placeholder="__('admin::forms.search')" v-model="filter.search" @change="updateResults" variant="filter" class="pl-10" />
                    </div>
                </div>

            </div>

        </form>

        <div class="flex flex-col mt-5">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200 table--index">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                                    {{ __('forms::labels.from') }}: {{ localeOptions[filter.locale_from] }}
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                                    {{ __('forms::labels.to') }}: {{ localeOptions[filter.locale_to] }}

                                    <span class="float-right inline-block" v-show="loading">
                                        <svg class="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    </span>
                                </th>
                            </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">

                            <tr v-for="(result, index) in results" :key="result.id">
                                <td class="px-6 py-4 text-sm font-medium text-gray-900 w-1/2">
                                    <span class="text-xs text-gray-500">
                                        {{ result.description }}
                                        <span class="inline-block ml-2 text-xs text-secondary" v-if="result.variables" v-for="variable in result.variables">
                                            :{{ variable }}
                                        </span>
                                    </span><br>
                                    {{ result.text_from }}
                                </td>
                                <td class="px-6 py-4 text-sm font-medium text-gray-900 w-1/2">
                                    <textarea
                                        class="appearance-none block w-full px-4 py-2 mt-1 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        :class="{
                                            'bg-gray-200': result.loading,
                                        }"
                                        @change="updateLanguageLine(index, filter.locale_to, $event)"
                                    >{{ result.text_to }}</textarea>
                                </td>
                            </tr>

                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

    </ModelIndexLayout>
</template>

<script>
import { AdjustmentsIcon, EyeIcon, SearchIcon, RefreshIcon } from '@heroicons/vue/solid'
import axios from 'axios'
import ModelIndexLayout from "@/Layouts/ModelIndexLayout";
import Input from "@/Components/Input";

export default {
    components: {
        Input,
        ModelIndexLayout,
        AdjustmentsIcon,
        EyeIcon,
        SearchIcon,
        RefreshIcon,
    },
    props: {
        results: Object,
        request: Object,
        localeOptions: Object,
        scopeOptions: Object,
        namespaceOptions: Object,
    },
    data() {
        return {
            filter: this.$inertia.form({
                search: this.request.search,
                locale_from: this.request.locale_from ?? 'en',
                locale_to: this.request.locale_to ?? 'en',
                scope: this.request.scope ?? 'all',
                namespace: this.request.namespace ?? '*',
            }),
            loading: false,
        }
    },
    methods: {
        updateResults() {
            this.loading = true;
            this.filter.get(this.$route('admin::language-lines.index'));
        },
        updateLanguageLine(key, localeTo, event) {

            let result = this.results[key];
            this.results[key].loading = true;

            let text = event.target.value;

            axios.patch(this.$route('admin::language-lines.update', { language_line: result.id } ), {
                locale: localeTo,
                text: text,
            })
                .then((res) => {
                    if (this.filter.locale_from === localeTo) {
                        this.results[key].text_from = text;
                    }
                    this.results[key].loading = false;
                });
        },
    },
}
</script>
